import { useState, useEffect } from "react";
import { Search, Loader2, CheckCircle2, XCircle, MapPin, UserPlus, Users, ChevronRight, Plus, Building2 } from "lucide-react";
import toast from "react-hot-toast";
import { AdminOrderManagementService } from "../../../services/api/admin-order.service";
import type { PatientAddress, PatientFamilyMember } from "../../../services/api/admin-order.types";
import AddAddressForm from "./AddAddressForm";
import AddFamilyMemberForm from "./AddFamilyMemberForm";
import type { PatientData, NearestClinicData, OrderForData, Step1Data } from "./types";

interface PatientDetailsStepProps {
  initialData: Step1Data | null;
  onNext: (data: Step1Data) => void;
}

const PatientDetailsStep: React.FC<PatientDetailsStepProps> = ({ initialData, onNext }) => {
  // ── Mobile Verification ──────────────────────────────────
  const [mobileNumber, setMobileNumber] = useState(initialData?.patient.patientMobileNumber || "");
  const [verifying, setVerifying] = useState(false);
  const [patient, setPatient] = useState<PatientData | null>(initialData?.patient || null);
  const [verifyError, setVerifyError] = useState("");

  // ── Register New Patient ─────────────────────────────────
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [registerForm, setRegisterForm] = useState({ name: "", gender: "", dateOfBirth: "" });
  const [registering, setRegistering] = useState(false);

  // ── Address Selection ────────────────────────────────────
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(initialData?.selectedAddress?.id || null);
  const [showAddAddress, setShowAddAddress] = useState(false);

  // ── Nearest Clinic ───────────────────────────────────────
  const [nearestClinic, setNearestClinic] = useState<NearestClinicData | null>(initialData?.nearestClinic || null);
  const [fetchingClinic, setFetchingClinic] = useState(false);
  const [clinicError, setClinicError] = useState("");

  // ── Order For ────────────────────────────────────────────
  const [orderFor, setOrderFor] = useState<OrderForData>(initialData?.orderFor || { type: "self" });
  const [showAddFamily, setShowAddFamily] = useState(false);

  // ── Auto-fetch nearest clinic when address changes ───────
  const selectedAddress = patient?.patientAddresses.find((a) => a.id === selectedAddressId) || null;

  useEffect(() => {
    if (selectedAddress && selectedAddress.latitude && selectedAddress.longitude) {
      fetchNearestClinic(selectedAddress);
    } else {
      setNearestClinic(null);
    }
  }, [selectedAddressId]);

  // ── Verify Mobile Number ─────────────────────────────────
  const handleVerify = async () => {
    if (!/^\d{10}$/.test(mobileNumber)) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }
    setVerifying(true);
    setVerifyError("");
    setPatient(null);
    setShowRegisterForm(false);
    setSelectedAddressId(null);
    setNearestClinic(null);
    setOrderFor({ type: "self" });

    try {
      const res = await AdminOrderManagementService.getPatientDetailsByMobile(mobileNumber);
      if (res.success && res.data) {
        setPatient(res.data);
        toast.success(`Patient found: ${res.data.patientName}`);
      } else {
        setVerifyError(res.message || "Patient not found");
        setShowRegisterForm(true);
      }
    } catch {
      setVerifyError("Failed to verify mobile number");
    } finally {
      setVerifying(false);
    }
  };

  // ── Register New Patient ─────────────────────────────────
  const handleRegister = async () => {
    if (!registerForm.name.trim() || !registerForm.gender || !registerForm.dateOfBirth) {
      toast.error("Please fill Name, Gender, and Date of Birth");
      return;
    }
    setRegistering(true);
    try {
      const res = await AdminOrderManagementService.registerNewPatient({
        mobileNumber,
        name: registerForm.name.trim(),
        gender: registerForm.gender.toLowerCase(),
        dateOfBirth: registerForm.dateOfBirth,
      });
      if (res.success && res.data) {
        toast.success("Patient registered successfully!");
        setPatient({
          patientId: res.data.id,
          patientName: res.data.name,
          patientMobileNumber: res.data.mobileNumber,
          patientAddresses: [],
          patientFamilyDetails: [],
        });
        setShowRegisterForm(false);
        setVerifyError("");
      } else {
        toast.error(res.message || "Failed to register patient");
      }
    } catch {
      toast.error("Failed to register patient");
    } finally {
      setRegistering(false);
    }
  };

  // ── Fetch Nearest Clinic ─────────────────────────────────
  const fetchNearestClinic = async (address: PatientAddress) => {
    setFetchingClinic(true);
    setClinicError("");
    setNearestClinic(null);
    try {
      const res = await AdminOrderManagementService.getNearestClinic({
        patientAddress: address.address,
        pincode: address.pincode,
        latitude: address.latitude,
        longitude: address.longitude,
      });
      if (res.success && res.data) {
        setNearestClinic(res.data);
      } else {
        setClinicError("No nearby clinic found for this address");
      }
    } catch {
      setClinicError("Failed to fetch nearest clinic");
    } finally {
      setFetchingClinic(false);
    }
  };

  // ── Handle New Address Added ─────────────────────────────
  const handleAddressAdded = (newAddress: PatientAddress) => {
    if (patient) {
      setPatient((prev) => prev ? { ...prev, patientAddresses: [...prev.patientAddresses, newAddress] } : prev);
      setSelectedAddressId(newAddress.id);
    }
    setShowAddAddress(false);
  };

  // ── Handle New Family Member Added ───────────────────────
  const handleFamilyMemberAdded = (member: PatientFamilyMember) => {
    if (patient) {
      setPatient((prev) => prev ? { ...prev, patientFamilyDetails: [...prev.patientFamilyDetails, member] } : prev);
      setOrderFor({ type: "family", familyMemberId: member.id, familyMemberName: member.name });
    }
    setShowAddFamily(false);
  };

  // ── Can Proceed? ─────────────────────────────────────────
  const canProceed = !!(patient && selectedAddress && nearestClinic && orderFor.type);

  const handleNext = () => {
    if (!patient || !selectedAddress || !nearestClinic) return;
    onNext({
      patient,
      selectedAddress,
      nearestClinic,
      orderFor,
    });
  };

  return (
    <div className="w-full">
      {/* ── Section 1: Mobile Number ─────────────────────────── */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 mb-4 shadow-sm">
        <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
          <Search size={16} className="text-emerald-500" />
          Patient Mobile Number
        </h3>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">+91</span>
            <input
              type="text"
              value={mobileNumber}
              onChange={(e) => {
                if (/^\d{0,10}$/.test(e.target.value)) setMobileNumber(e.target.value);
              }}
              placeholder="Enter 10-digit mobile number"
              className="w-full pl-10 pr-3 py-2.5 text-xs border border-slate-200 rounded-lg outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 bg-slate-50/50 font-semibold"
              onKeyDown={(e) => e.key === "Enter" && handleVerify()}
            />
          </div>
          <button
            onClick={handleVerify}
            disabled={verifying || mobileNumber.length !== 10}
            className="px-5 py-2.5 text-xs font-bold bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-lg hover:shadow-md transition-all border-none cursor-pointer disabled:opacity-40 flex items-center gap-1.5"
          >
            {verifying ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
            {verifying ? "Verifying..." : "Verify"}
          </button>
        </div>

        {/* Patient Found Card */}
        {patient && (
          <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-3">
            <CheckCircle2 size={18} className="text-emerald-500 flex-shrink-0" />
            <div>
              <p className="text-xs font-bold text-emerald-700">{patient.patientName}</p>
              <p className="text-[0.65rem] text-emerald-600">Mobile: {patient.patientMobileNumber}</p>
            </div>
          </div>
        )}

        {/* Patient Not Found + Register Form */}
        {verifyError && !patient && (
          <div className="mt-3">
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 mb-3">
              <XCircle size={18} className="text-red-400 flex-shrink-0" />
              <p className="text-xs font-semibold text-red-600">{verifyError}</p>
            </div>

            {showRegisterForm && (
              <div className="border border-amber-200 rounded-xl bg-amber-50/30 p-4">
                <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <UserPlus size={14} className="text-amber-500" />
                  Register New Patient
                </h4>
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div>
                    <label className="text-[0.68rem] font-semibold text-slate-500 block mb-1">Full Name *</label>
                    <input
                      type="text"
                      value={registerForm.name}
                      onChange={(e) => setRegisterForm((p) => ({ ...p, name: e.target.value }))}
                      placeholder="Patient name"
                      className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 bg-white"
                    />
                  </div>
                  <div>
                    <label className="text-[0.68rem] font-semibold text-slate-500 block mb-1">Gender *</label>
                    <select
                      value={registerForm.gender}
                      onChange={(e) => setRegisterForm((p) => ({ ...p, gender: e.target.value }))}
                      className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 bg-white cursor-pointer"
                    >
                      <option value="">Select</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[0.68rem] font-semibold text-slate-500 block mb-1">Date of Birth *</label>
                    <input
                      type="date"
                      value={registerForm.dateOfBirth}
                      onChange={(e) => setRegisterForm((p) => ({ ...p, dateOfBirth: e.target.value }))}
                      max={new Date().toISOString().split("T")[0]}
                      className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 bg-white"
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={handleRegister}
                    disabled={registering}
                    className="px-4 py-2 text-xs font-bold bg-amber-500 text-white rounded-lg hover:bg-amber-600 border-none cursor-pointer transition-colors disabled:opacity-50 flex items-center gap-1.5"
                  >
                    {registering ? <Loader2 size={12} className="animate-spin" /> : <UserPlus size={12} />}
                    {registering ? "Registering..." : "Register Patient"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Sections 2 & 3: Delivery Address & Order For in Two Halves ── */}
      {patient && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-4">
          {/* Left Half: Delivery Address */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                  <MapPin size={16} className="text-emerald-500" />
                  Delivery Address
                </h3>
                <button
                  onClick={() => setShowAddAddress(!showAddAddress)}
                  className="px-3 py-1.5 text-xs font-semibold bg-emerald-50 text-emerald-600 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-all cursor-pointer flex items-center gap-1.5"
                >
                  <Plus size={12} /> Add New Address
                </button>
              </div>

              {patient.patientAddresses.length === 0 && !showAddAddress ? (
                <div className="text-center py-6 text-slate-400">
                  <MapPin size={24} className="mx-auto mb-2 text-slate-300" />
                  <p className="text-xs font-semibold">No saved addresses found</p>
                  <p className="text-[0.65rem] mt-1">Click "Add New Address" to add one</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1" style={{ scrollbarWidth: "thin" }}>
                  {patient.patientAddresses.map((addr) => (
                    <label
                      key={addr.id}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${selectedAddressId === addr.id
                          ? "border-emerald-400 bg-emerald-50/50 ring-1 ring-emerald-200"
                          : "border-slate-200 hover:border-slate-300 hover:bg-slate-50/50"
                        }`}
                    >
                      <input
                        type="radio"
                        name="address"
                        checked={selectedAddressId === addr.id}
                        onChange={() => setSelectedAddressId(addr.id)}
                        className="mt-0.5 accent-emerald-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-slate-700">{addr.addressName || "Address"}</span>
                          {addr.isDefault && (
                            <span className="px-1.5 py-0.5 text-[0.55rem] font-bold bg-emerald-100 text-emerald-600 rounded">DEFAULT</span>
                          )}
                        </div>
                        <p className="text-[0.68rem] text-slate-500 mt-0.5 leading-relaxed">{addr.address}</p>
                        <p className="text-[0.62rem] text-slate-400 mt-0.5">{addr.city}, {addr.state} — {addr.pincode}</p>
                      </div>
                    </label>
                  ))}
                </div>
              )}

              {showAddAddress && (
                <AddAddressForm
                  patientId={patient.patientId}
                  onAddressAdded={handleAddressAdded}
                  onCancel={() => setShowAddAddress(false)}
                />
              )}
            </div>

            {/* Nearest Clinic Info */}
            {selectedAddress && (
              <div className="mt-4 p-3 rounded-lg border border-slate-200 bg-slate-50/50">
                <div className="flex items-center gap-2 mb-1">
                  <Building2 size={14} className="text-slate-400" />
                  <span className="text-[0.68rem] font-bold text-slate-500 uppercase tracking-wider">Nearest Clinic</span>
                </div>
                {fetchingClinic ? (
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <Loader2 size={12} className="animate-spin" /> Fetching nearest clinic...
                  </div>
                ) : nearestClinic ? (
                  <div className="flex items-center gap-3">
                    <p className="text-xs font-bold text-emerald-600">{nearestClinic.clinicName || `Clinic ID: ${nearestClinic.clinicId}`}</p>
                    <span className="text-[0.65rem] text-slate-400">• {nearestClinic.distance.toFixed(2)} km away</span>
                  </div>
                ) : clinicError ? (
                  <p className="text-xs text-red-500 font-semibold">{clinicError}</p>
                ) : null}
              </div>
            )}
          </div>

          {/* Right Half: Order For */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex flex-col justify-between">
            <div>
              <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                <Users size={16} className="text-emerald-500" />
                Order For
              </h3>

              <div className="flex gap-3 mb-4">
                <label
                  className={`flex-1 flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${orderFor.type === "self"
                      ? "border-emerald-400 bg-emerald-50/50 ring-1 ring-emerald-200"
                      : "border-slate-200 hover:border-slate-300"
                    }`}
                >
                  <input
                    type="radio"
                    name="orderFor"
                    checked={orderFor.type === "self"}
                    onChange={() => setOrderFor({ type: "self" })}
                    className="accent-emerald-500"
                  />
                  <div>
                    <p className="text-xs font-bold text-slate-700">Self</p>
                    <p className="text-[0.62rem] text-slate-400">Order for {patient.patientName}</p>
                  </div>
                </label>
                <label
                  className={`flex-1 flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${orderFor.type === "family"
                      ? "border-indigo-400 bg-indigo-50/50 ring-1 ring-indigo-200"
                      : "border-slate-200 hover:border-slate-300"
                    }`}
                >
                  <input
                    type="radio"
                    name="orderFor"
                    checked={orderFor.type === "family"}
                    onChange={() => setOrderFor({ type: "family" })}
                    className="accent-indigo-500"
                  />
                  <div>
                    <p className="text-xs font-bold text-slate-700">Family Member</p>
                    <p className="text-[0.62rem] text-slate-400">Order for a family member</p>
                  </div>
                </label>
              </div>

              {/* Family Member List */}
              {orderFor.type === "family" && (
                <>
                  {patient.patientFamilyDetails.length === 0 && !showAddFamily ? (
                    <div className="text-center py-4 text-slate-400">
                      <Users size={20} className="mx-auto mb-1.5 text-slate-300" />
                      <p className="text-xs font-semibold">No family members found</p>
                      <p className="text-[0.65rem] mt-1">Add a family member to continue</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1 mb-3" style={{ scrollbarWidth: "thin" }}>
                      {patient.patientFamilyDetails.map((member) => (
                        <label
                          key={member.id}
                          className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${orderFor.familyMemberId === member.id
                              ? "border-indigo-400 bg-indigo-50/50 ring-1 ring-indigo-200"
                              : "border-slate-200 hover:border-slate-300"
                            }`}
                        >
                          <input
                            type="radio"
                            name="familyMember"
                            checked={orderFor.familyMemberId === member.id}
                            onChange={() => setOrderFor({ type: "family", familyMemberId: member.id, familyMemberName: member.name })}
                            className="accent-indigo-500"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-bold text-slate-700">{member.name}</p>
                            <p className="text-[0.62rem] text-slate-400">{member.relation} • {member.gender}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}

                  <button
                    onClick={() => setShowAddFamily(!showAddFamily)}
                    className="px-3 py-1.5 text-xs font-semibold bg-indigo-50 text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-all cursor-pointer flex items-center gap-1.5"
                  >
                    <Plus size={12} /> Add New Family Member
                  </button>

                  {showAddFamily && (
                    <AddFamilyMemberForm
                      patientId={patient.patientId}
                      onMemberAdded={handleFamilyMemberAdded}
                      onCancel={() => setShowAddFamily(false)}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Next Step Button ─────────────────────────────────── */}
      {patient && (
        <div className="flex justify-end mt-4">
          <button
            onClick={handleNext}
            disabled={!canProceed || (orderFor.type === "family" && !orderFor.familyMemberId)}
            className="px-6 py-3 text-sm font-bold bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-xl hover:shadow-lg transition-all border-none cursor-pointer disabled:opacity-40 flex items-center gap-2"
          >
            Next: Select Medicines
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default PatientDetailsStep;
